import { ConsentStatusEnum } from '@pypestream/api-services';
import { usePostHog } from 'posthog-js/react';
import { FC, PropsWithChildren, useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { useTranslation } from '@pypestream/translations';
import { IPage } from '../pages/types';
import { initialTools, syncXstateWithRouteParams } from '../utils';
import {
  useManagerCtxSelector,
  useManagerStateMatches,
  useUserCtxSelector,
} from '../xstate/app.xstate';
import { sendUserMessage } from '../xstate/app.xstate-utils';

export const RouteDataProvider: FC<PropsWithChildren> = ({ children }) => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { org_id: orgIdQueryParam } = params;
  const { orgId, orgs } = useManagerCtxSelector((ctx) => ({
    orgId: ctx.selectedOrgId,
    orgs: ctx.orgs,
  }));
  const { defaultOrgId } = useUserCtxSelector((ctx) => ({
    defaultOrgId: ctx.user?.defaultAccount?.defaultOrgId,
  }));
  const currentOrgState = useManagerStateMatches('orgRelated.ready.currentOrg');
  const { optionalConsentStatus } = useUserCtxSelector((ctx) => ({
    optionalConsentStatus: ctx.user?.optionalConsentStatus,
  }));

  useEffect(() => {
    if (currentOrgState) {
      syncXstateWithRouteParams({ params });
    }
  }, [params, currentOrgState]);

  useEffect(() => {
    const redirectedDueToNoToolAccess = searchParams.get(
      'redirectedDueToNoToolAccess'
    );
    const app = searchParams.get('app');
    const appName = initialTools.find((tool) => tool.name === app)?.label;
    if (redirectedDueToNoToolAccess && appName) {
      navigate(pathname, { replace: true });
      sendUserMessage({
        type: 'error',
        text: t('manager/common:homepage.noToolAccessMessage', {
          appName,
        }),
        autoClose: 3000,
      });
    }
  }, [searchParams, pathname, navigate, t]);

  useEffect(() => {
    if (optionalConsentStatus === ConsentStatusEnum.Accepted) {
      posthog.capture('$pageview');
    }

    if (orgId && !orgIdQueryParam) {
      navigate(`/organization/${orgId}${pathname}`, { replace: true });
    }
  }, [
    pathname,
    orgId,
    optionalConsentStatus,
    posthog,
    orgIdQueryParam,
    navigate,
  ]);

  useEffect(() => {
    if (!orgs?.length || !orgIdQueryParam || !defaultOrgId) return;
    const org = orgs?.find((o) => o.id === orgIdQueryParam);
    if (!org) {
      navigate(`/organization/${defaultOrgId}`, { replace: true });
    }
  }, [orgs, orgIdQueryParam, defaultOrgId, navigate]);

  return <>{children}</>;
};

export function WithRouteData<T extends IPage>(
  WrappedComponent: React.ComponentType<T>
) {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithRouteData = (props: T) => {
    return <WrappedComponent {...props} />;
  };

  ComponentWithRouteData.displayName = `WithRouteData(${displayName})`;

  return ComponentWithRouteData;
}
